import React from "react";

import { observer } from "mobx-react";
import styles from "../styles.css";
import settingStyles from "../../../../styles/settings.css";
import classNames from "classnames";
import { useStore } from "../../../../models";
import { ElementForm } from "../../settingsForms/elementForm/elementForm/elementForm";
import { VerticalSeparator } from "../../settingsForms/separators/verticalSeparator/verticalSeparator";
import { ElementFormActions } from "../../settingsForms/elementForm/actions/elementFormActions";
import { ElementFormEditIcon } from "../../settingsForms/elementForm/icons/elementFormEditIcon";
import { ElementFormDeleteIcon } from "../../settingsForms/elementForm/icons/elementFormDeleteIcon";
import { EditInput } from "@fidget/common/components/form/editInput";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { ElementFormSaveDiscardIcon } from "../../settingsForms/elementForm/icons/elementFormSaveDiscardIcon";
import { DeleteConfirmationToastOnError } from "../../settingsForms/deleteConfirmation/deleteConfirmation";
import { useAgentEditStatus } from "../editStatus/useEditStatus";
import { AgentEditStatusForm } from "../editStatus/types";

export const AgentStatusList = observer(() => {
  const { uiStore } = useStore();

  const { settings } = useAgentEditStatus();

  const handleDeleteItem = async (id: string) => {
    try {
      await settings.apiDeleteItem(id);
      settings.uiRemoveExistingItem(id);
    } catch (e) {
      uiStore.addGenericErrorToast();
    }
  };

  const handleUpdateItem = async (item: AgentEditStatusForm) => {
    try {
      await settings.updateExistingItem(item);
      settings.saveEditedItemChangesLocally();
    } catch (e) {
      uiStore.addGenericErrorToast();
    }
  };

  const renderEditingItem = (item: AgentEditStatusForm) => {
    return (
      <ElementForm key={item.$.id.$}>
        <div className={classNames(styles["form-element"])}>
          <span className={classNames(settingStyles["form-label-font"])}>
            {intl.formatMessage("settings.adviserStatuses.form.reasonSlug.label")}
          </span>
          <p className={classNames(settingStyles["form-content-font"], styles.value)}>
            {item.$.reasonSlug.$}
          </p>
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-element"])}>
          <span className={classNames(settingStyles["form-label-font"])}>
            {intl.formatMessage("settings.adviserStatuses.form.reason.label")}
          </span>
          <EditInput
            placeholder={intl.formatMessage("settings.adviserStatuses.form.placeholder")}
            className={classNames(styles["form-input"])}
            autoFocus
            value={item.$.reason.$}
            onChange={(event) => item.$.reason.reset((event.target as HTMLInputElement).value)}
            hasError={item.$.reason.hasError}
            errorHint={item.$.reason.error}
          />
        </div>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormSaveDiscardIcon
            onSave={() => handleUpdateItem(item)}
            onDiscard={settings.discardEditedItem}
          />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderViewItem = (item: AgentEditStatusForm, onDeleteClick: () => void) => {
    return (
      <ElementForm key={item.$.id.$}>
        <div className={classNames(styles["form-element"])}>
          <span className={classNames(settingStyles["form-label-font"])}>
            {intl.formatMessage("settings.adviserStatuses.form.reasonSlug.label")}
          </span>
          <p className={classNames(settingStyles["form-content-font"], styles.value)}>
            {item.$.reasonSlug.$}
          </p>
        </div>
        <VerticalSeparator />
        <div className={classNames(styles["form-element"])}>
          <span className={classNames(settingStyles["form-label-font"])}>
            {intl.formatMessage("settings.adviserStatuses.form.reason.label")}
          </span>
          <p className={classNames(settingStyles["form-content-font"], styles.value)}>
            {item.$.reason.$}
          </p>
        </div>
        <VerticalSeparator />
        <ElementFormActions>
          <ElementFormEditIcon onClick={() => settings.onExistingEditClick(item)} />
          <ElementFormDeleteIcon onClick={onDeleteClick} />
        </ElementFormActions>
      </ElementForm>
    );
  };

  const renderItem = (item: AgentEditStatusForm) => {
    const { currentlyEditedItem } = settings;
    const itemId = item.$.id.$;

    return currentlyEditedItem?.$.id.$ === itemId ? (
      renderEditingItem(currentlyEditedItem)
    ) : (
      <DeleteConfirmationToastOnError
        key={itemId}
        areYouSureText={intl.formatMessage("settings.adviserStatuses.removeText")}
        succesfullyDeletedText={intl.formatMessage("settings.adviserStatuses.removedSuccessfully")}
        onTimeoutPassed={() => settings.uiRemoveExistingItem(itemId)}
        onDelete={() => handleDeleteItem(itemId)}
        renderComponent={(onDeleteClick) => renderViewItem(item, onDeleteClick)}
      />
    );
  };

  return <>{settings.items.map((item) => renderItem(item))}</>;
});
